import { createContext, Dispatch, PropsWithChildren, SetStateAction, useContext, useState, useEffect } from "react";

import { type BoundingBoxStyleOptions } from "@/src/components/pdf-viewer/canvas-utils";
import { type ExtractionMetadata } from "@/src/models/section";

interface PDFViewerContext {
  metadata: ExtractionMetadata | null;
  setMetadata: Dispatch<SetStateAction<ExtractionMetadata | null>>;
  clearMetadata: () => void;
  activePage: number | null;
  setActivePage: Dispatch<SetStateAction<number | null>>;
  boundingBoxStyle: BoundingBoxStyleOptions;
  setBoundingBoxStyle: Dispatch<SetStateAction<BoundingBoxStyleOptions>>;
  focusedBoundingBoxIndex: number | null;
  setFocusedBoundingBoxIndex: Dispatch<SetStateAction<number | null>>;
  focusNextMetadata: () => void;
  focusPrevMetadata: () => void;
  hideNonFocusedBoxes: boolean;
  setHideNonFocusedBoxes: Dispatch<SetStateAction<boolean>>;
  toggleHideNonFocusedBoxes: () => void;
}

const PDFViewerContext = createContext<PDFViewerContext>({
  metadata: null,
  setMetadata: () => {},
  clearMetadata: () => {},
  activePage: null,
  setActivePage: () => {},
  boundingBoxStyle: {},
  setBoundingBoxStyle: () => {},
  focusedBoundingBoxIndex: null,
  setFocusedBoundingBoxIndex: () => {},
  focusNextMetadata: () => {},
  focusPrevMetadata: () => {},
  hideNonFocusedBoxes: false,
  setHideNonFocusedBoxes: () => {},
  toggleHideNonFocusedBoxes: () => {},
});

export function usePdfViewerInteractionContext(defaultBoundingBoxStyle?: BoundingBoxStyleOptions): PDFViewerContext {
  const context = useContext(PDFViewerContext);
  const { setBoundingBoxStyle } = context;

  useEffect(() => {
    if (defaultBoundingBoxStyle) {
      setBoundingBoxStyle(defaultBoundingBoxStyle);
    }
  }, [defaultBoundingBoxStyle, setBoundingBoxStyle]);

  return context;
}

export function PDFViewerInteractionProvider({ enabled = true, children }: PropsWithChildren<{ enabled?: boolean }>) {
  const [metadataState, setMetadataState] = useState<{
    metadata: ExtractionMetadata | null;
    focusedBoundingBoxIndex: number | null;
  }>({ metadata: null, focusedBoundingBoxIndex: null });
  const [activePage, setActivePage] = useState<number | null>(null);
  const [boundingBoxStyle, setBoundingBoxStyle] = useState<BoundingBoxStyleOptions>({});
  const [hideNonFocusedBoxes, setHideNonFocusedBoxes] = useState<boolean>(false);
  const { metadata, focusedBoundingBoxIndex } = metadataState;

  const setMetadata: Dispatch<SetStateAction<ExtractionMetadata | null>> = (nextMetadata) => {
    setMetadataState((previous) => {
      const metadata = typeof nextMetadata === "function" ? nextMetadata(previous.metadata) : nextMetadata;

      if (Object.is(metadata, previous.metadata)) return previous;

      return {
        metadata,
        focusedBoundingBoxIndex: metadata?.bboxes?.length ? 0 : null,
      };
    });
  };

  const setFocusedBoundingBoxIndex: Dispatch<SetStateAction<number | null>> = (nextIndex) => {
    setMetadataState((previous) => {
      const focusedBoundingBoxIndex =
        typeof nextIndex === "function" ? nextIndex(previous.focusedBoundingBoxIndex) : nextIndex;

      if (Object.is(focusedBoundingBoxIndex, previous.focusedBoundingBoxIndex)) return previous;

      return { ...previous, focusedBoundingBoxIndex };
    });
  };

  const clearMetadata = () => {
    setMetadata(null);
  };

  const focusNextMetadata = () => {
    setFocusedBoundingBoxIndex((prevIndex) => {
      if (!metadata?.bboxes || metadata.bboxes.length === 0) {
        return null;
      }

      const currentIndex = typeof prevIndex === "number" ? prevIndex : -1;
      const nextIndex = (currentIndex + 1) % metadata.bboxes.length;

      return nextIndex;
    });
  };

  const focusPrevMetadata = () => {
    setFocusedBoundingBoxIndex((prevIndex) => {
      if (!metadata?.bboxes || metadata.bboxes.length === 0) {
        return null;
      }

      const currentIndex = typeof prevIndex === "number" ? prevIndex : 0;
      const nextIndex = (currentIndex - 1 + metadata.bboxes.length) % metadata.bboxes.length;

      return nextIndex;
    });
  };

  const toggleHideNonFocusedBoxes = () => {
    setHideNonFocusedBoxes((prev) => !prev);
  };

  const context = (() => {
    return {
      metadata: enabled ? metadata : null,
      setMetadata,
      clearMetadata,
      activePage: enabled ? activePage : null,
      setActivePage,
      boundingBoxStyle,
      setBoundingBoxStyle,
      focusedBoundingBoxIndex,
      setFocusedBoundingBoxIndex,
      focusNextMetadata,
      focusPrevMetadata,
      hideNonFocusedBoxes,
      setHideNonFocusedBoxes,
      toggleHideNonFocusedBoxes,
    };
  })();

  return <PDFViewerContext.Provider value={context}>{children}</PDFViewerContext.Provider>;
}
